import React from 'react';
import { Stack, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { NumericFormat } from 'react-number-format';

// Potential future upgrades here: use a variant prop to determine whether an item is from "My Card" or "Peer Card" or both,
// and assign variables accordingly, instead of the current approach of checking whether the arguments exist.
// This way we can assume if we have a certain variant, that certain arguments/props will always be present.
function ComparisonItemEntry({ myItem, myRec, peerItem, seeItemDetails, physicianNames = {}, isSelected }) {
    const theme = useTheme();

    // Check to see if we have a myItem, myRec, or peerItem to display
    let primaryItem = myItem && Object.keys(myItem).length > 0 ? myItem : null;
    if (!primaryItem && myRec?.item && Object.keys(myRec.item).length > 0) {
        primaryItem = myRec.item;
    }
    if (!primaryItem && peerItem && Object.keys(peerItem).length > 0) {
        primaryItem = peerItem;
    }

    // Handles all cases for displaying open/hold quantities or removed status based on myRec status/type
    let open_quantity = null;
    let hold_quantity = null;
    let specialCaseText = null;
    let specialCaseColor = 'red';
    if (myRec) {
        if (myRec.status === 'custom_swap' || myRec.status === 'custom_update' || myRec.status === 'custom_addition') {
            open_quantity = myRec.custom_open ?? null;
            hold_quantity = myRec.custom_hold ?? null;
        } else if (myRec.status === 'accepted') {
            open_quantity = myRec.open_quantity;
            hold_quantity = myRec.hold_quantity;
        }
        
        if (myRec.status === 'custom_removal' || (myRec.status === 'accepted' && myRec.type === 'removal')) {
            specialCaseText = 'Item Removed';
        } else if (myRec.status === 'rejected' && myRec.type === 'addition') {
            specialCaseText = 'Addition Declined';
        } else if (myRec.status === 'open' && myRec.type === 'addition') {
            specialCaseText = 'Add To Card';
            specialCaseColor = 'green';
        }
        // Handles rendering correct item when myRec is a swap or compliance that has been accepted.
        if ((myRec.type === 'swap' || myRec.type === 'compliance') && (myRec.status === 'accepted') || (myRec.status === 'custom_swap')) {
            primaryItem = myRec.recommended_item;
        }
    } else {
        open_quantity = primaryItem.open_quantity ?? null;
        hold_quantity = primaryItem.hold_quantity ?? null;
    }

    return (
        <Stack
            sx={{
                borderRadius: 1,
                background: theme.palette.background.white,
                overflow: 'hidden',
                border: isSelected ? `1.5px solid ${theme.palette.primary.main}` : '1px solid transparent',
                boxShadow: isSelected ? theme.shadows[4] : theme.shadows[1],
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: theme.shadows[4],
                    border: `1.5px solid ${theme.palette.primary.main}`,
                },
            }}
            onClick={() => seeItemDetails(primaryItem)}
        >
            {/* Top: name, vendor, cost, arrow */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="inherit" p={1.5}>
                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        variant="heavy"
                        fontSize='12px'
                        noWrap
                        sx={{ textOverflow: 'ellipsis' }}
                    >
                        {primaryItem.display_name || primaryItem.name}
                    </Typography>
                    <Typography variant="normal" fontSize='12px' noWrap>
                        {primaryItem.vendor.name}
                        <span style={{ color: theme.palette.subtext.main, fontSize: '14px' }}>  |  </span>
                        <NumericFormat
                            value={primaryItem.cost / 100}
                            thousandSeparator={true}
                            displayType={'text'}
                            prefix={'$'}
                            decimalScale={2}
                            fixedDecimalScale={true}
                        />
                    </Typography>
                </Stack>
                <ArrowForwardIosRoundedIcon fontSize='small' sx={{ color: theme.palette.subtext.main }} />
            </Stack>
            
            {/* Bottom: My Card / Peer Card OR Dr. ___: open | hold */}
            <Stack  direction="row" width="inherit" alignItems="center" sx={{ bgcolor: theme.palette.background.darkGray }}>
                {/* My Card */}
                <Stack p={1} direction="row" alignItems="center" justifyContent='center' spacing={0.5} flex={1} >
                    <Typography variant="heavy" fontSize='12px'>{physicianNames?.first || 'My Card'}</Typography>
                    { !myItem &&
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.background.neutral, color: theme.palette.text.primary, p: 0.5, borderRadius: 1 }}>Not On Card</Typography>
                    }
                    { myItem && specialCaseText && specialCaseColor === 'green' && 
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.itemSwapChip.background.positive, color: theme.palette.itemSwapChip.text.positive, p: 0.5, borderRadius: 1 }}>{specialCaseText}</Typography>
                    }
                    { myItem && specialCaseText && specialCaseColor === 'red' &&
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.itemSwapChip.background.negative, color: theme.palette.itemSwapChip.text.negative, p: 0.5, borderRadius: 1 }}>{specialCaseText}</Typography>
                    }
                    {myItem && !specialCaseText &&
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.itemSwapChip.background.positive, color: theme.palette.itemSwapChip.text.positive, p: 0.5, borderRadius: 1 }}>Open: {open_quantity ?? myItem.open_quantity} | Hold: {hold_quantity ?? myItem.hold_quantity}</Typography>
                    }
                </Stack>
                {/* Divider */}
                <Divider orientation="vertical" flexItem />
                {/* Peer Card */}
                <Stack p={1} direction="row" alignItems="center" justifyContent='center' spacing={0.5} flex={1}>
                    <Typography variant="heavy" fontSize='12px'>{physicianNames?.second || 'Peer'}</Typography>
                    {peerItem ? (
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.itemSwapChip.background.positive, color: theme.palette.itemSwapChip.text.positive, p: 0.5, borderRadius: 1, ml: 1 }}>Open: {peerItem.open_quantity} | Hold: {peerItem.hold_quantity}</Typography>
                    ) : (
                        <Typography variant="heavy" fontSize='12px' sx={{ bgcolor: theme.palette.background.neutral, color: theme.palette.text.primary, p: 0.5, borderRadius: 1, ml: 1 }}>Not On Card</Typography>
                    )}
                </Stack>
            </Stack>
        </Stack>
    )
}

export default ComparisonItemEntry;